import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import config from '../../../config/environment';
import DogParkLogin from './DogParkLogin';

const AUTH_API = 'https://api.belvoircastle.com/api';
const DOG_PARK_SESSION_KEY = 'dog_park_admin_ok';

const verifyDataCentreToken = async (token) => {
  if (!token) return false;
  try {
    const res = await fetch(`${AUTH_API}/test-auth`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return res.ok;
  } catch (err) {
    return false;
  }
};

const Wrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem 3rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #101d40;

  @media print {
    padding: 0;
    max-width: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media print {
    display: none;
  }
`;

const TitleBlock = styled.div`
  h1 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 700;
  }
  p {
    margin: 0;
    color: rgba(16, 29, 64, 0.58);
    font-size: 0.95rem;
    line-height: 1.45;
  }
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.15rem;

  @media print {
    display: none;
  }
`;

const Tab = styled.button`
  border: 1px solid ${props => (props.$active ? '#101d40' : 'rgba(16, 29, 64, 0.14)')};
  background: ${props => (props.$active ? '#101d40' : '#fff')};
  color: ${props => (props.$active ? '#fff' : '#101d40')};
  border-radius: 999px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: end;
  margin-bottom: 1.15rem;

  @media print {
    display: none;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.55);

  input, select, textarea {
    font-family: inherit;
    font-size: 0.95rem;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: #101d40;
    border: 1px solid rgba(16, 29, 64, 0.16);
    border-radius: 10px;
    padding: 0.55rem 0.7rem;
    min-width: 140px;
    background: #fff;
  }

  textarea {
    min-width: 220px;
    min-height: 70px;
  }
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.62rem 1rem;
  font-weight: 650;
  font-size: 0.92rem;
  cursor: pointer;
  background: ${props => (props.$secondary ? '#fff' : '#9d6d49')};
  color: ${props => (props.$secondary ? '#101d40' : '#fff')};
  border: 1px solid ${props => (props.$secondary ? 'rgba(16, 29, 64, 0.16)' : '#9d6d49')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.15rem;

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media print {
    display: none;
  }
`;

const Stat = styled.div`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 14px;
  padding: 0.9rem 1rem;
  box-shadow: 0 8px 24px rgba(16, 29, 64, 0.04);

  strong {
    display: block;
    font-size: 1.45rem;
    line-height: 1.1;
    margin-bottom: 0.2rem;
  }
  span {
    font-size: 0.8rem;
    color: rgba(16, 29, 64, 0.55);
  }
`;

const DayCard = styled.section`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(16, 29, 64, 0.04);

  @media print {
    box-shadow: none;
    break-inside: avoid;
    border: 1px solid #ccc;
  }
`;

const DayHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  background: #f7f5f1;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);

  h2 {
    margin: 0;
    font-size: 1.05rem;
  }
  span {
    font-size: 0.85rem;
    color: rgba(16, 29, 64, 0.58);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;

  th, td {
    text-align: left;
    padding: 0.7rem 0.85rem;
    border-bottom: 1px solid rgba(16, 29, 64, 0.07);
    vertical-align: top;
  }

  th {
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(16, 29, 64, 0.48);
    background: #fbfaf8;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const CodeChip = styled.span`
  display: inline-block;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: #101d40;
  color: #fff;
  border-radius: 8px;
  padding: 0.22rem 0.5rem;
  font-size: 0.88rem;
`;

const Empty = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: rgba(16, 29, 64, 0.5);
`;

const FormCard = styled.form`
  background: #fff;
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  padding: 1.1rem;
  margin-bottom: 1.15rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #101d40;
`;

const ErrorText = styled.p`
  color: #a71d2a;
  background: rgba(167, 29, 42, 0.07);
  border: 1px solid rgba(167, 29, 42, 0.16);
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  font-size: 0.9rem;
`;

const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysYmd = (ymd, days) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const formatDay = (ymd) => {
  const [y, m, d] = (ymd || '').split('-').map(Number);
  if (!y) return ymd || '';
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `£${n.toFixed(2)}`;
};

const emptyCodeForm = {
  id: null,
  label: '',
  validFrom: todayYmd(),
  validTo: addDaysYmd(todayYmd(), 6),
  timeFrom: '',
  timeTo: '',
  gateCode: '',
  notes: '',
  active: true,
};

const DogParkBookings = () => {
  const [authState, setAuthState] = useState('checking'); // checking | login | ready
  const [tab, setTab] = useState('bookings');
  const [dateFrom, setDateFrom] = useState(todayYmd());
  const [dateTo, setDateTo] = useState(addDaysYmd(todayYmd(), 14));
  const [search, setSearch] = useState('');
  const [bookings, setBookings] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeForm, setCodeForm] = useState(emptyCodeForm);
  const [savingCode, setSavingCode] = useState(false);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
        limit: '500',
      });
      const res = await fetch(`${config.apiEndpoint}/custom/v1/get-dog-park-bookings?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load bookings');
      setBookings(data.bookings || []);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadCodes = useCallback(async () => {
    try {
      const res = await fetch(`${config.apiEndpoint}/custom/v1/get-dog-park-gate-codes`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load gate codes');
      setCodes(data.codes || []);
    } catch (err) {
      setError(err.message || 'Failed to load gate codes');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const unlockedThisSession = sessionStorage.getItem(DOG_PARK_SESSION_KEY) === '1';
      if (!token || !unlockedThisSession) {
        if (!cancelled) setAuthState('login');
        return;
      }
      const valid = await verifyDataCentreToken(token);
      if (cancelled) return;
      if (valid) {
        setAuthState('ready');
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem(DOG_PARK_SESSION_KEY);
        setAuthState('login');
      }
    };
    checkAuth();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authState !== 'ready') return;
    loadBookings();
    loadCodes();
  }, [authState, loadBookings, loadCodes]);

  const handleLogout = () => {
    sessionStorage.removeItem(DOG_PARK_SESSION_KEY);
    setAuthState('login');
  };

  const filteredBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) => {
      const hay = [
        b.customer_name,
        b.customer_email,
        b.customer_phone,
        b.car_registration,
        b.booking_reference,
        b.gate_code_shown,
        b.current_gate_code,
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [bookings, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    filteredBookings.forEach((booking) => {
      const key = booking.visit_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(booking);
    });
    return Array.from(map.entries());
  }, [filteredBookings]);

  const totals = useMemo(() => {
    return filteredBookings.reduce(
      (acc, b) => {
        acc.bookings += 1;
        acc.dogs += Number(b.total_dogs || 0);
        acc.extras += Number(b.extra_dogs || 0);
        acc.amount += Number(b.total_amount || 0);
        return acc;
      },
      { bookings: 0, dogs: 0, extras: 0, amount: 0 }
    );
  }, [filteredBookings]);

  // — conditional returns AFTER all hooks —
  if (authState === 'checking') {
    return (
      <Wrap>
        <Empty>Checking Data Centre login…</Empty>
      </Wrap>
    );
  }

  if (authState !== 'ready') {
    return (
      <DogParkLogin
        onSuccess={() => {
          sessionStorage.setItem(DOG_PARK_SESSION_KEY, '1');
          setAuthState('ready');
        }}
      />
    );
  }

  const printSheet = (dayKey = null) => {
    const rows = dayKey
      ? filteredBookings.filter((b) => b.visit_date === dayKey)
      : filteredBookings;
    const title = dayKey ? `Dog Park — ${formatDay(dayKey)}` : 'Dog Park bookings';
    const groupedRows = {};
    rows.forEach((b) => {
      if (!groupedRows[b.visit_date]) groupedRows[b.visit_date] = [];
      groupedRows[b.visit_date].push(b);
    });

    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Georgia, 'Times New Roman', serif; color: #111; margin: 24px; }
            h1 { font-size: 22px; margin: 0 0 4px; }
            h2 { font-size: 16px; margin: 22px 0 8px; }
            p { margin: 0 0 12px; color: #444; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; }
            th, td { border: 1px solid #bbb; padding: 7px 8px; text-align: left; vertical-align: top; }
            th { background: #f3f1ec; text-transform: uppercase; letter-spacing: 0.04em; font-size: 11px; }
            .code { font-family: Menlo, monospace; font-weight: 700; }
            .meta { font-size: 12px; color: #555; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p class="meta">Printed ${new Date().toLocaleString('en-GB')} · ${rows.length} booking${rows.length === 1 ? '' : 's'} · ${rows.reduce((s, b) => s + Number(b.total_dogs || 0), 0)} dogs</p>
          ${Object.entries(groupedRows).map(([date, list]) => `
            <h2>${formatDay(date)}</h2>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Car reg</th>
                  <th>Dogs</th>
                  <th>Extra dogs</th>
                  <th>Gate code</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                ${list.map((b) => `
                  <tr>
                    <td>${b.visit_time || '—'}</td>
                    <td>${b.customer_name || ''}</td>
                    <td>${b.customer_email || ''}${b.customer_phone ? `<br>${b.customer_phone}` : ''}</td>
                    <td class="code">${b.car_registration || '—'}</td>
                    <td>${b.total_dogs || 1}</td>
                    <td>${b.extra_dogs || 0}</td>
                    <td class="code">${b.current_gate_code || b.gate_code_shown || '—'}</td>
                    <td>${b.booking_reference || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `).join('')}
        </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const saveCode = async (e) => {
    e.preventDefault();
    setSavingCode(true);
    setError('');
    try {
      const res = await fetch(`${config.apiEndpoint}/custom/v1/save-dog-park-gate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(codeForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save gate code');
      setCodeForm(emptyCodeForm);
      await loadCodes();
    } catch (err) {
      setError(err.message || 'Failed to save gate code');
    } finally {
      setSavingCode(false);
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Remove this gate code period?')) return;
    setError('');
    try {
      const res = await fetch(`${config.apiEndpoint}/custom/v1/delete-dog-park-gate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete gate code');
      if (codeForm.id === id) setCodeForm(emptyCodeForm);
      await loadCodes();
    } catch (err) {
      setError(err.message || 'Failed to delete gate code');
    }
  };

  return (
    <Wrap>
      <Header>
        <TitleBlock>
          <h1>Dog Park bookings</h1>
          <p>Live list of Dog Park slots, extra dogs, and the gate code that applies to each visit.</p>
        </TitleBlock>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {tab === 'bookings' && (
            <Button type="button" onClick={() => printSheet()}>Print list</Button>
          )}
          <Button type="button" $secondary onClick={handleLogout}>Log out</Button>
        </div>
      </Header>

      <TabRow>
        <Tab type="button" $active={tab === 'bookings'} onClick={() => setTab('bookings')}>Bookings</Tab>
        <Tab type="button" $active={tab === 'codes'} onClick={() => setTab('codes')}>Gate codes</Tab>
      </TabRow>

      {error && <ErrorText>{error}</ErrorText>}

      {tab === 'bookings' && (
        <>
          <Toolbar>
            <Field>
              From
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Field>
            <Field>
              To
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Field>
            <Field>
              Search
              <input
                type="search"
                placeholder="Name, email, reference…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Field>
            <Button type="button" $secondary onClick={loadBookings} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </Toolbar>

          <Stats>
            <Stat><strong>{totals.bookings}</strong><span>Bookings</span></Stat>
            <Stat><strong>{totals.dogs}</strong><span>Total dogs</span></Stat>
            <Stat><strong>{totals.extras}</strong><span>Extra dogs</span></Stat>
            <Stat><strong>{formatMoney(totals.amount)}</strong><span>Takings</span></Stat>
          </Stats>

          {grouped.length === 0 ? (
            <Empty>{loading ? 'Loading bookings…' : 'No Dog Park bookings in this date range.'}</Empty>
          ) : grouped.map(([date, list]) => {
            const dogs = list.reduce((sum, b) => sum + Number(b.total_dogs || 0), 0);
            return (
              <DayCard key={date}>
                <DayHead>
                  <div>
                    <h2>{formatDay(date)}</h2>
                    <span>{list.length} booking{list.length === 1 ? '' : 's'} · {dogs} dog{dogs === 1 ? '' : 's'}</span>
                  </div>
                  <Button type="button" $secondary onClick={() => printSheet(date)}>Print day</Button>
                </DayHead>
                <Table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Customer</th>
                      <th>Car reg</th>
                      <th>Dogs</th>
                      <th>Extra dogs</th>
                      <th>Gate code</th>
                      <th>Paid</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((booking) => (
                      <tr key={booking.id}>
                        <td>{booking.visit_time || '—'}</td>
                        <td>
                          <strong>{booking.customer_name}</strong><br />
                          {booking.customer_email}
                          {booking.customer_phone ? <><br />{booking.customer_phone}</> : null}
                          {booking.admin_notes ? <><br /><em>{booking.admin_notes}</em></> : null}
                        </td>
                        <td>
                          <CodeChip>
                            {booking.car_registration || '—'}
                          </CodeChip>
                        </td>
                        <td>{booking.total_dogs}</td>
                        <td>{booking.extra_dogs || 0}</td>
                        <td>
                          <CodeChip>
                            {booking.current_gate_code || booking.gate_code_shown || '—'}
                          </CodeChip>
                        </td>
                        <td>{formatMoney(booking.total_amount)}</td>
                        <td>{booking.booking_reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DayCard>
            );
          })}
        </>
      )}

      {tab === 'codes' && (
        <>
          <FormCard onSubmit={saveCode}>
            <Field>
              Label
              <input
                value={codeForm.label}
                onChange={(e) => setCodeForm({ ...codeForm, label: e.target.value })}
                placeholder="e.g. Week 1 morning"
              />
            </Field>
            <Field>
              Gate code
              <input
                required
                value={codeForm.gateCode}
                onChange={(e) => setCodeForm({ ...codeForm, gateCode: e.target.value })}
                placeholder="e.g. 2468"
              />
            </Field>
            <Field>
              Valid from
              <input
                type="date"
                required
                value={codeForm.validFrom}
                onChange={(e) => setCodeForm({ ...codeForm, validFrom: e.target.value })}
              />
            </Field>
            <Field>
              Valid to
              <input
                type="date"
                required
                value={codeForm.validTo}
                onChange={(e) => setCodeForm({ ...codeForm, validTo: e.target.value })}
              />
            </Field>
            <Field>
              Time from (optional)
              <input
                type="time"
                value={codeForm.timeFrom}
                onChange={(e) => setCodeForm({ ...codeForm, timeFrom: e.target.value })}
              />
            </Field>
            <Field>
              Time to (optional)
              <input
                type="time"
                value={codeForm.timeTo}
                onChange={(e) => setCodeForm({ ...codeForm, timeTo: e.target.value })}
              />
            </Field>
            <Field>
              Notes
              <input
                value={codeForm.notes}
                onChange={(e) => setCodeForm({ ...codeForm, notes: e.target.value })}
                placeholder="Optional staff note"
              />
            </Field>
            <CheckRow>
              <input
                type="checkbox"
                checked={codeForm.active}
                onChange={(e) => setCodeForm({ ...codeForm, active: e.target.checked })}
              />
              Active
            </CheckRow>
            <Button type="submit" disabled={savingCode}>
              {codeForm.id ? 'Update code' : 'Save code'}
            </Button>
            {codeForm.id && (
              <Button
                type="button"
                $secondary
                onClick={() => setCodeForm(emptyCodeForm)}
              >
                Cancel edit
              </Button>
            )}
          </FormCard>

          <DayCard>
            <DayHead>
              <h2>Saved periods</h2>
              <span>The matching active code is what appears on the booking list and print sheet.</span>
            </DayHead>
            {codes.length === 0 ? (
              <Empty>No gate codes yet. Add a period above.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Dates</th>
                    <th>Times</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => (
                    <tr key={code.id}>
                      <td>{code.label || '—'}</td>
                      <td>{formatDay(code.valid_from)} – {formatDay(code.valid_to)}</td>
                      <td>
                        {code.time_from || code.time_to
                          ? `${code.time_from || 'start'}–${code.time_to || 'end'}`
                          : 'All day'}
                      </td>
                      <td><CodeChip>{code.gate_code}</CodeChip></td>
                      <td>{Number(code.active) === 1 ? 'Active' : 'Off'}</td>
                      <td>
                        <Button
                          type="button"
                          $secondary
                          onClick={() => setCodeForm({
                            id: Number(code.id),
                            label: code.label || '',
                            validFrom: code.valid_from,
                            validTo: code.valid_to,
                            timeFrom: code.time_from || '',
                            timeTo: code.time_to || '',
                            gateCode: code.gate_code || '',
                            notes: code.notes || '',
                            active: Number(code.active) === 1,
                          })}
                        >
                          Edit
                        </Button>
                        {' '}
                        <Button type="button" $secondary onClick={() => deleteCode(code.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </DayCard>
        </>
      )}
    </Wrap>
  );
};

export default DogParkBookings;
